// frontend/src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

interface User {
  userId: string;
  role: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null; // Deprecated - kept for backward compatibility
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  isLoading: boolean;
  error: string | null;
  sessionExpired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// A 401 from the API means the session is gone (the 4 h cookie expired and
// the browser dropped it, or the session was revoked) - except these codes,
// which mean a wrong password typed into a form.
const CREDENTIAL_CODES = new Set(['INVALID_CREDENTIALS', 'INVALID_CURRENT_PASSWORD']);

const API_PREFIX = new URL(API_BASE_URL, window.location.origin).href.replace(/\/$/, '') + '/';
const isApiUrl = (url: string) => {
  try {
    return new URL(url, window.location.origin).href.startsWith(API_PREFIX);
  } catch {
    return false;
  }
};

// Check the session when the user comes back after a while (tab shown, window
// focused, first click or key), so an expired session lands on the login popup
// before a tool is started rather than as an error after.
const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Drop the user so ProtectedRoute sends them to the login popup, which
  // returns them to the page they were on. Only acts while logged in, so the
  // anonymous auth check on page load and parallel 401s are no-ops.
  const expireSession = useCallback(() => {
    if (!userRef.current) return;
    userRef.current = null;
    setSessionExpired(true);
    setToken(null);
    setUser(null);
    setError(null);
    // Clear a cookie that may still be set (revoked session).
    axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  }, []);

  useEffect(() => {
    // Check if user is authenticated by trying to fetch user data
    // The httpOnly cookie will be sent automatically
    const checkAuth = async () => {
      console.log('🔍 Checking authentication on page load...');
      console.log('📍 API URL:', API_BASE_URL);

      try {
        const response = await axios.get(`${API_BASE_URL}/user`, {
          withCredentials: true // Include httpOnly cookie
        });

        console.log('✅ Auth check response:', response.status);

        if (response.data.user) {
          setUser(response.data.user);
          setToken('cookie-based'); // Placeholder since actual token is in httpOnly cookie
          console.log('✅ User authenticated:', response.data.user.userId);
        } else {
          console.log('⚠️ No user data in response');
        }
      } catch (error: any) {
        console.error('❌ Auth check failed');
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          code: error.code,
          url: error.config?.url
        });

        // Set error state for network issues so UI can show it
        if (error.code === 'ERR_NETWORK' || error.code === 'ENOTFOUND' || !error.response) {
          setError('Cannot connect to server. Please check your connection.');
          console.error('🌐 Network error - cannot reach API');
        } else if (error.response?.status === 404) {
          setError('Authentication endpoint not found. Please contact support.');
          console.error('🔍 Endpoint /user not found - check backend routes');
        } else if (error.response?.status === 401) {
          console.log('🔓 No active session (401)');
          // Not authenticated - this is normal, don't set error
        } else {
          console.log('ℹ️ Not authenticated or session expired');
        }
      }
      setIsLoading(false);
      console.log('✓ Auth check complete');
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isApiUrl(error.config?.url || '') &&
            !CREDENTIAL_CODES.has(error.response.data?.code)) {
          expireSession();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [expireSession]);

  // Most tools call the API with fetch, which the axios interceptor never sees.
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await originalFetch(input, init);
      if (response.status === 401) {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        if (isApiUrl(url)) {
          response.clone().json()
            .then((body) => { if (!CREDENTIAL_CODES.has(body?.code)) expireSession(); })
            .catch(() => expireSession());
        }
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [expireSession]);

  useEffect(() => {
    let lastCheck = Date.now();
    const check = () => {
      if (!userRef.current || document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck < SESSION_CHECK_INTERVAL_MS) return;
      lastCheck = Date.now();
      // A 401 here expires the session through the interceptor.
      axios.get(`${API_BASE_URL}/user`, { withCredentials: true }).catch(() => {});
    };

    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    document.addEventListener('pointerdown', check, true);
    document.addEventListener('keydown', check, true);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('pointerdown', check, true);
      document.removeEventListener('keydown', check, true);
    };
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // Include credentials for httpOnly cookie
        timeout: 10000 // 10 second timeout
      });

      const { user } = response.data;

      // Token is now in httpOnly cookie, not in response body
      setToken('cookie-based'); // Placeholder
      setUser(user);
      setSessionExpired(false);

      console.log('Authentication successful, token stored in httpOnly cookie');
      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });

      if (error.code === 'ENOTFOUND' || error.message.includes('Network Error')) {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError(error.response?.data?.error || error.message || 'Login failed');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Call logout endpoint to clear httpOnly cookie
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    setToken(null);
    setUser(null);
    setError(null);
  };

  const hasPermission = (permission: string): boolean => {
    // Admin role grants all permissions.
    if (user?.role === 'admin') {
      return true;
    }
    const perms = user?.permissions;
    if (!perms) return false;
    // Exact hierarchical match (e.g. 'urban_wiki:view').
    if (perms.includes(permission)) return true;
    // The admin panel grants FLAT tool names ('urban_wiki'), while the gates
    // ask for a hierarchical action ('urban_wiki:view'). Treat a bare tool
    // permission as granting its actions, so a user granted 'urban_wiki'
    // satisfies 'urban_wiki:view'. (Mutations are gated on admin role, not on
    // ':manage', so this cannot over-grant.)
    const tool = permission.split(':')[0];
    if (perms.includes(tool)) return true;
    // The tool was renamed building_analyzer -> street_analyzer (Gatuanalys);
    // users granted before the rename still carry the old strings.
    if (tool === 'street_analyzer') {
      const action = permission.split(':')[1];
      return perms.includes('building_analyzer') ||
        (!!action && perms.includes(`building_analyzer:${action}`));
    }
    return false;
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    hasPermission,
    isLoading,
    error,
    sessionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
